// The six group slots of the season map (R1817; mockup round 085 cards
// 10, 12 A, 13 and 14; ui-map-interface.md §8 "The control-group
// controls").
//
// A slot shows what its control group holds: the digit, the hull mark of
// the class in the reader's ship icon set (R1859), the short name, the state and a hull bar. The state reads by shape: the
// order glyph while all is well, a triangle for a weak hull, an anchor in
// the pens (the slot stays, R839), a dashed edge for an empty slot, and a
// lit plate with an accent bar for the selected fleet.
//
// Two hosts draw the same slots. A telephone draws the **group row**, one
// row of 44 px over the tab bar in the closed dock, only while a slot holds
// a fleet (085 open point 4). A desktop draws the **group bar** under the
// readout line, for the mouse player too, always: the digit as a key cap,
// and the first word of the name, or the whole name on a wide screen.
//
// Both hosts show the slots that hold a fleet and ONE empty slot, the empty
// slot with the lowest digit, so a new group can always be assigned (the
// owner, 2026-10-09, R1865). The slots keep their digits and their order.
// With no group held, the bar shows the empty slot 1 alone (the owner,
// 2026-10-13: "always one empty slot", R2057), and the row stands not at
// all (R1956 ruling 3).
//
// A press recalls the group, as the digit key does. A long press (the long
// press of the game, with a ring that fills round the finger) assigns the
// selected fleet to the slot; the overlay owns what an assign says. On the
// group row of a telephone (R2415) a long press on the slot of the
// selected fleet spends its signature order and assigns nothing, and a
// long press on a slot that holds another fleet assigns only after
// `override_hold_ms` more; an empty slot keeps the plain long press.
//
// The views and the markup are pure. `SlotHost` and `SlotToast` are the two
// small DOM pieces; they hold nodes and timers and decide nothing.

import { Mission, type ShipStock } from '../gen/proto/aquaphobia';
import { GROUP_SLOTS_UI, KEYS } from '../gen/presentation';
import { overlay as S } from '../gen/strings';
import { GROUP_SLOTS, type ControlGroups } from '../groups';
import type { SceneLook } from '../render/scene-look';
import { shipMarkSvg } from './cards/marks';
import { esc } from './esc';
import { fillPct, hullBand } from './format';
import type { UIFleet } from './overlay/types';
import { TAP_DEFAULTS, TapRecognizer } from './touch';
import { toChromePx } from './uiscale';

/** The state of a slot, which its shape states. */
export type SlotState = 'empty' | 'held' | 'weak' | 'dock';

/** One slot as both hosts draw it. */
export interface SlotView {
  n: number;
  fleetId: number;
  state: SlotState;
  /** The slot's fleet is the selection. */
  sel: boolean;
  name: string;
  /** The hull mark of the class (`shipMarkSvg`), as markup, or '' for an
   *  empty slot. */
  mark: string;
  mission: Mission;
  /** The hull left, 0 to 1. */
  hull: number;
}

/**
 * The short name of a slot (085 open point 2): the first word of the fleet
 * name, cut to `short_name_len` letters, in capitals. No field stores it,
 * so a rename shows at once.
 */
export function shortName(name: string, len: number = GROUP_SLOTS_UI.shortNameLen): string {
  return [...firstWord(name)].slice(0, len).join('').toUpperCase();
}

/** The shape of a hull designator (R44): the faction letter, then SN for a
 *  combat hull or SV for a support hull. */
const DESIGNATOR = /^[A-Z]S[NV]$/;

/**
 * A fleet name without its designator (R1924). The server writes the
 * designator in front of every hull name, a renamed one too ("ASN Narwhal
 * II"), so the first word of a name is the same three letters on every
 * hull of a faction. A name with no designator stays whole.
 */
export function namePart(name: string): string {
  const t = name.trim();
  const cut = t.search(/\s/);
  return cut > 0 && DESIGNATOR.test(t.slice(0, cut)) ? t.slice(cut).trim() : t;
}

/** The first word of a fleet name after its designator: the name a narrow
 *  bar slot shows. */
export function firstWord(name: string): string {
  return namePart(name).split(/\s+/)[0] ?? '';
}

/** Does a desktop of this width show the whole name in a bar slot? */
export function barWide(width: number): boolean {
  return width >= GROUP_SLOTS_UI.barWideMinPx;
}

/** The width of the group bar in chrome px: `n` slots of the narrow or
 *  the wide width, and the gaps between them. */
export function barWidth(n: number, wide: boolean, gap: number): number {
  if (n <= 0) return 0;
  return n * (wide ? GROUP_SLOTS_UI.barSlotWidePx : GROUP_SLOTS_UI.barSlotPx) + (n - 1) * gap;
}

/** The form of the group bar in its room. */
export interface BarFit {
  /** The slots name the whole fleet. */
  wide: boolean;
  /** The bar starts at the left edge of its room, not centred. */
  tight: boolean;
}

/**
 * The form of a bar of `n` slots (R1865): the wide names on a screen of
 * `bar_wide_min_px` or more where the wide bar fits its room, else the
 * narrow names; and the left start where even the narrow bar is wider than
 * its room, as the readout line's `readout-tight` form (R1868), so the
 * first slot stays on the screen. `vw` and `room` are chrome px.
 */
export function barFit(n: number, vw: number, room: number, gap: number): BarFit {
  const wide = barWide(vw) && barWidth(n, true, gap) <= room;
  return { wide, tight: barWidth(n, false, gap) > room };
}

/** What the views read of the state. */
export interface SlotInput {
  groups: ControlGroups;
  /** The own fleets on the map. */
  sea: readonly UIFleet[];
  /** The own hulls in the pens. */
  docked: readonly ShipStock[];
  /** The selected fleet, or 0. */
  selected: number;
  /** The reader's look, whose ship icon set draws the hull mark, as the
   *  fleet row of FLEET · MISSIONS draws it (R1844, R1859). */
  look?: SceneLook;
}

/**
 * The six slots, in order. A slot whose fleet is neither on the map nor
 * in the pens (a pledged hull, a hull the next frame drops) draws as
 * empty: the client knows nothing it could show of it.
 */
export function slotViews(i: SlotInput): SlotView[] {
  const out: SlotView[] = [];
  for (let n = 1; n <= GROUP_SLOTS; n++) {
    const id = i.groups.fleetAt(n);
    const f = id ? i.sea.find((x) => x.id === id) : undefined;
    const st = id && !f ? i.docked.find((x) => x.fleetId === id) : undefined;
    if (f) {
      out.push({
        n, fleetId: id, state: hullBand(f.hullPct) === 'bad' ? 'weak' : 'held', sel: i.selected === id,
        name: f.name, mark: shipMarkSvg(f.cls, i.look), mission: f.mission, hull: f.hullPct / 100,
      });
    } else if (st) {
      // A docked fleet is selected too: its card is the deploy ask (R1988).
      out.push({
        n, fleetId: id, state: 'dock', sel: i.selected === id, name: st.name, mark: shipMarkSvg(st.cls, i.look),
        mission: Mission.MISSION_UNSPECIFIED, hull: (100 - st.wearPct) / 100,
      });
    } else {
      out.push({
        n, fleetId: 0, state: 'empty', sel: false, name: '', mark: '',
        mission: Mission.MISSION_UNSPECIFIED, hull: 0,
      });
    }
  }
  return out;
}

/**
 * The slots a host shows (R1865): every slot that holds a fleet, at sea or
 * in the pens, and the empty slot with the lowest digit, in the order of
 * the digits. With all six held, no empty slot shows.
 */
export function shownSlots(views: readonly SlotView[]): SlotView[] {
  const spare = spareSlot(views);
  return views.filter((v) => v.state !== 'empty' || v.n === spare);
}

/** The digit of the one empty slot a host shows: the empty slot with the
 *  lowest digit, or 0 with all six held. */
function spareSlot(views: readonly SlotView[]): number {
  for (const v of views) if (v.state === 'empty') return v.n;
  return 0;
}

/** The digit of the slot button an event target stands in, or 0. */
function slotOf(t: EventTarget | null): number {
  return Number((t as HTMLElement).closest<HTMLElement>('[data-slot]')?.dataset.slot ?? 0);
}

/** The group row of a telephone stands only while a slot holds a fleet
 *  (R1817, R1956); the group bar of a desktop stands always (R2057). */
export function rowShown(views: readonly SlotView[]): boolean {
  return views.some((v) => v.state !== 'empty');
}

/** The key chord that assigns slot `n` on a desktop: `ctrl-1`. */
export function assignKey(n: number): string {
  return (KEYS.map.group_assign.claims[n - 1] ?? '').replace('Digit', '');
}

// --- the state glyphs (12 px line icons in a 24-unit box) ---

const GLYPH = {
  weak: '<path d="M12 4l9 16H3z"/><path d="M12 10v4.5M12 17v.6"/>',
  dock: '<circle cx="12" cy="5" r="2"/><path d="M12 7v13M8 11h8M5 14a7 7 0 0 0 14 0"/>',
  gather: '<path d="M8 6l4 6-4 6-4-6zM16 6l4 6-4 6-4-6z"/>',
  patrol: '<path d="M5.5 12a6.5 6.5 0 0 1 11.3-4.4M18.5 12a6.5 6.5 0 0 1-11.3 4.4"/>'
    + '<path d="M17.6 3.8v4.1h-4.1M6.4 20.2v-4.1h4.1"/>',
  defend: '<path d="M12 3.5l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9v-5z"/>',
  move: '<path d="M4 12h15M13 6l6 6-6 6"/>',
  hunt: '<circle cx="12" cy="12" r="6.5"/><path d="M12 3v4M12 17v4M3 12h4M17 12h4"/>',
  home: '<path d="M4 12l8-7 8 7M7 10v9h10v-9"/>',
  escort: '<path d="M6 6l6 6-6 6M13 6l6 6-6 6"/>',
  anchor: '<path d="M12 21V9M8 4h8l-4 5z"/>',
  idle: '<circle cx="12" cy="12" r="2.5"/>',
} as const;

/** The order glyph of a mission. */
function orderGlyph(m: Mission): keyof typeof GLYPH {
  switch (m) {
    case Mission.MISSION_GATHER: return 'gather';
    case Mission.MISSION_PATROL: return 'patrol';
    case Mission.MISSION_DEFEND: return 'defend';
    // SEARCH sails to a point first (R2649).
    case Mission.MISSION_MOVE: case Mission.MISSION_STEER: case Mission.MISSION_SEARCH: return 'move';
    case Mission.MISSION_ATTACK_MOVE: return 'hunt';
    case Mission.MISSION_RETURN: case Mission.MISSION_HOME: return 'home';
    case Mission.MISSION_ESCORT: return 'escort';
    case Mission.MISSION_ANCHOR: return 'anchor';
    default: return 'idle';
  }
}

/** The state glyph of a slot: a weak hull and the pens win over the order. */
function stateGlyph(v: SlotView): string {
  if (v.state === 'empty') return '';
  const g = v.state === 'weak' ? 'weak' : v.state === 'dock' ? 'dock' : orderGlyph(v.mission);
  return `<svg class="gs-sg ${g}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"`
    + ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${GLYPH[g]}</svg>`;
}

/** The classes of a slot button. */
function slotClass(v: SlotView): string {
  return `gs ${v.state}${v.sel ? ' sel' : ''}`;
}

/**
 * The inside of a slot. `chip` is the telephone chip of 44 px: the digit,
 * the hull mark and the state glyph over the short name. `key` is the
 * desktop slot: the digit as a key cap, the hull mark, the name (the
 * first word, or the whole name when `wide`) and the state glyph. Both end
 * in the hull bar, hatched in the pens. The bar takes the health colours of
 * the fleet list: the hero ink, amber in the middle band, the bad ink when
 * weak (R2213).
 */
export function slotInner(v: SlotView, mode: 'chip' | 'key', wide = false): string {
  const band = v.state === 'weak' ? ' low' : v.state === 'held' && hullBand(v.hull * 100) === 'gold' ? ' mid' : '';
  const bar = `<i class="gs-bar${band}${v.state === 'dock' ? ' hatch' : ''}"><b></b></i>`;
  const mark = `<span class="gs-mk">${v.state === 'empty' ? '' : v.mark}</span>`;
  if (mode === 'chip') {
    const name = v.state === 'empty' ? S.slot_empty : shortName(v.name);
    return `<span class="gs-l1"><span class="gs-dg">${v.n}</span>${mark}${stateGlyph(v)}</span>`
      + `<span class="gs-sn">${esc(name)}</span>${bar}`;
  }
  const name = v.state === 'empty'
    ? S.slot_empty_key({ key: assignKey(v.n) })
    : wide ? v.name : firstWord(v.name);
  return `<kbd class="gs-key">${v.n}</kbd>${mark}<span class="gs-sn">${esc(name)}</span>`
    + `<span class="gs-st">${stateGlyph(v)}</span>${bar}`;
}

/** The title of a slot: what it holds and what a press does. */
function slotTitle(v: SlotView): string {
  return v.state === 'empty'
    ? S.slot_empty_title({ n: v.n, key: assignKey(v.n) })
    : S.slot_title({ name: v.name });
}

// --- the long press on the group row (R2415) --------------------------------

/**
 * What a long press on a slot of the telephone group row does: `sign`
 * spends the signature order of the selected fleet, `override` assigns
 * after the longer hold because the slot holds another fleet, and `assign`
 * is the plain long press of an empty slot. `sign` needs a host that spends
 * the order; without one the slot of the selection assigns as before.
 */
export type HoldKind = 'assign' | 'sign' | 'override';

/** The kind of a long press on slot view `v` (R2415). */
export function holdKind(v: SlotView | undefined, canSign: boolean): HoldKind {
  if (!v || v.state === 'empty') return 'assign';
  if (v.sel) return canSign ? 'sign' : 'assign';
  return 'override';
}

/** How long a long press of kind `k` rests before it acts, in ms. */
export function holdMs(k: HoldKind): number {
  return TAP_DEFAULTS.longPressMs + (k === 'override' ? GROUP_SLOTS_UI.overrideHoldMs : 0);
}

// --- the DOM hosts --------------------------------------------------------

/** The empty slot of a host that draws it in its own form: the inside
 *  and the accessible name. The arcade's group row draws its empty slot
 *  as one hold square (R2053); the season hosts pass none. */
export type SpareSlot = (v: SlotView) => { html: string; label: string };

/** What a slot host tells its owner. */
export interface SlotHooks {
  /** A short press: recall the group. */
  tap(n: number): void;
  /** A long press: assign the selection to the slot. */
  hold(n: number): void;
  /** A long press on the slot of the selected fleet on the group row of
   *  a telephone (R2415): spend its signature order. A host with no hook
   *  assigns there as everywhere. */
  sign?(n: number): void;
  /** A mouse came onto a slot, or left the host (n 0). */
  hover(n: number, el: HTMLElement | null): void;
}

/**
 * One host of six slot buttons: the group row or the group bar.
 *
 * The buttons are built once and kept, and a render writes only the parts
 * that changed: a long press must keep its button under the finger for
 * the whole 450 ms, and a re-render that replaced the button would drop
 * the press. A slot outside the shown set (`shownSlots`) is hidden, never
 * removed, and the slot under an active press stays shown until the press
 * ends, so a change of the set cannot take the button from the finger. A slot acts on the RELEASE, through the press recogniser of
 * the map (`ui/touch.ts`): the same button answers a press and a long
 * press, so it cannot act at the first contact (§16.8 names the hold as
 * the case that waits for the release, as VIEW does).
 */
export class SlotHost {
  readonly el: HTMLElement;
  private readonly btns: HTMLButtonElement[] = [];
  private readonly drawn: string[] = [];
  private readonly tap = new TapRecognizer();
  /** The slot the finger came down on, or 0. */
  private down = 0;
  private downAt = { x: 0, y: 0 };
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** The timer of the override hold (R2415), which assigns at its end. */
  private overTimer: ReturnType<typeof setTimeout> | null = null;
  private ring: HTMLElement | null = null;
  /** The views of the last render: the press reads its slot's kind. */
  private views: readonly SlotView[] = [];
  /** What the long press of the press now does. */
  private kind: HoldKind = 'assign';

  constructor(id: string, private readonly mode: 'chip' | 'key', private readonly hooks: SlotHooks,
    private readonly ringHost: () => HTMLElement | null, private readonly spare?: SpareSlot) {
    this.el = document.createElement('div');
    this.el.id = id;
    this.el.setAttribute('role', 'group');
    this.el.setAttribute('aria-label', S.groups_label);
    for (let n = 1; n <= GROUP_SLOTS; n++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.dataset.slot = String(n);
      this.btns.push(b);
      this.drawn.push('');
      this.el.appendChild(b);
    }
    this.tap.onTap = () => { const n = this.down; this.end(); if (n) this.hooks.tap(n); };
    this.tap.onLongPress = () => {
      const n = this.down;
      // An override keeps the press, the ring and its own timer: the
      // press past the long press is no tap now, and no assign yet.
      if (n && this.kind === 'override') return;
      const kind = this.kind;
      this.end();
      if (!n) return;
      if (kind === 'sign' && this.hooks.sign) this.hooks.sign(n);
      else this.hooks.hold(n);
    };
    this.el.addEventListener('pointerdown', (e) => this.onDown(e));
    this.el.addEventListener('pointermove', (e) => this.onMove(e));
    this.el.addEventListener('pointerup', (e) => {
      this.tap.up(e.pointerId, e.clientX, e.clientY, e.timeStamp);
      // A release before the end of an override hold assigns nothing.
      this.end();
    });
    this.el.addEventListener('pointercancel', (e) => { this.tap.cancel(e.pointerId); this.end(); });
    // A long press must not open the callout or the menu of the system.
    this.el.addEventListener('contextmenu', (e) => e.preventDefault());
    this.el.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const n = slotOf(e.target);
      if (!n) return;
      e.preventDefault();
      this.hooks.tap(n);
    });
    this.el.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse') return;
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-slot]');
      if (b) this.hooks.hover(Number(b.dataset.slot), b);
    });
    this.el.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hooks.hover(0, null); });
  }

  /** Writes the slots, each button only when its markup changed, and
   *  hides each slot outside the shown set. */
  render(views: readonly SlotView[], wide = false) {
    this.views = views;
    const spare = spareSlot(views);
    for (const v of views) {
      const b = this.btns[v.n - 1];
      if (!b) continue;
      const hide = v.state === 'empty' && v.n !== spare && this.down !== v.n;
      if (b.hidden !== hide) b.hidden = hide;
      const own = this.spare && v.state === 'empty' ? this.spare(v) : null;
      const html = own ? own.html : slotInner(v, this.mode, wide);
      const cls = own ? `${slotClass(v)} spare` : slotClass(v);
      const hull = fillPct(v.hull);
      const key = `${cls}|${hull}|${html}`;
      if (this.drawn[v.n - 1] === key) continue;
      this.drawn[v.n - 1] = key;
      b.className = cls;
      b.innerHTML = html;
      b.title = own ? own.label : slotTitle(v);
      // The spare slot shows no words, so its name states the long press.
      if (own) b.setAttribute('aria-label', own.label);
      else b.removeAttribute('aria-label');
      b.style.setProperty('--gs-hull', hull);
    }
  }

  /** The button of slot `n`, for a drop target and a test. */
  button(n: number): HTMLButtonElement | undefined { return this.btns[n - 1]; }

  private onDown(e: PointerEvent) {
    if (e.button > 0) return;
    const n = slotOf(e.target);
    if (!n) return;
    this.end();
    this.down = n;
    this.kind = this.mode === 'chip' ? holdKind(this.views.find((v) => v.n === n), !!this.hooks.sign) : 'assign';
    this.downAt = { x: e.clientX, y: e.clientY };
    this.tap.down(e.pointerId, e.clientX, e.clientY, e.timeStamp);
    // The release must come back here even when the finger slides off.
    try { this.el.setPointerCapture(e.pointerId); } catch { /* a synthetic pointer */ }
    // The long press fires while the finger still rests (`touch.ts`). The
    // timer firing is the proof that the rest lasted, so it states the
    // press's own clock plus the hold, and the recogniser never compares
    // two clocks (R583).
    const at = e.timeStamp;
    this.timer = setTimeout(() => this.tap.tick(at + TAP_DEFAULTS.longPressMs), TAP_DEFAULTS.longPressMs);
    const hold = holdMs(this.kind);
    if (this.kind === 'override') {
      this.overTimer = setTimeout(() => { this.end(); this.hooks.hold(n); }, hold);
    }
    this.showRing(e.clientX, e.clientY, hold);
  }

  private onMove(e: PointerEvent) {
    if (!this.down) return;
    this.tap.move(e.pointerId, e.clientX, e.clientY, e.timeStamp);
    // A drag past the slop voids the press, and the ring goes with it.
    if (Math.hypot(e.clientX - this.downAt.x, e.clientY - this.downAt.y) > TAP_DEFAULTS.slopPx) this.end();
  }

  /** The ring that fills round the finger for the length of a long press. */
  private showRing(x: number, y: number, hold: number) {
    const host = this.ringHost();
    if (!host) return;
    const r = document.createElement('div');
    r.className = 'gs-ring';
    r.style.left = `${toChromePx(x)}px`;
    r.style.top = `${toChromePx(y)}px`;
    r.style.setProperty('--hold', `${hold}ms`);
    r.innerHTML = '<svg viewBox="0 0 68 68" aria-hidden="true"><circle class="t" cx="34" cy="34" r="30"/>'
      + '<circle class="f" cx="34" cy="34" r="30"/></svg>';
    host.appendChild(r);
    this.ring = r;
  }

  private end() {
    this.down = 0;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    if (this.overTimer !== null) clearTimeout(this.overTimer);
    this.overTimer = null;
    this.ring?.remove();
    this.ring = null;
  }
}

/**
 * The drag of a card head onto a slot of the group bar (085 card 13, the
 * desktop frame). A mouse press on the head of the selected own fleet's
 * card, carried past the slop, takes a ghost with the fleet's name; the
 * slot under the pointer lights, and the release over it assigns. Every
 * listener of a drag lives on the window only while the drag runs.
 */
export class HeadDrag {
  private ghost: HTMLElement | null = null;
  private lit: HTMLElement | null = null;

  /**
   * `card` is the host of the selection card, `name` the name of the own
   * fleet the card shows (or null), and `drop` the assign of a slot.
   */
  constructor(card: HTMLElement, private readonly bar: HTMLElement,
    private readonly name: () => string | null, private readonly drop: (n: number) => void,
    private readonly ghostHost: () => HTMLElement | null) {
    card.addEventListener('pointerdown', (e) => this.onDown(e));
  }

  private onDown(e: PointerEvent) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const t = e.target as HTMLElement;
    if (!t.closest('.ck-head') || t.closest('button')) return;
    const name = this.name();
    if (!name) return;
    const x0 = e.clientX, y0 = e.clientY;
    const move = (m: PointerEvent) => {
      if (!this.ghost) {
        if (Math.hypot(m.clientX - x0, m.clientY - y0) <= TAP_DEFAULTS.slopPx) return;
        this.ghost = document.createElement('div');
        this.ghost.className = 'gs-ghost';
        this.ghost.textContent = name;
        this.ghostHost()?.appendChild(this.ghost);
      }
      this.ghost.style.left = `${toChromePx(m.clientX)}px`;
      this.ghost.style.top = `${toChromePx(m.clientY)}px`;
      this.light(this.slotAt(m.clientX, m.clientY));
    };
    const up = (u: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const target = this.ghost ? this.slotAt(u.clientX, u.clientY) : null;
      this.ghost?.remove();
      this.ghost = null;
      this.light(null);
      const n = Number(target?.dataset.slot ?? 0);
      if (n) this.drop(n);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** The slot button of the bar under a point, or null. */
  private slotAt(x: number, y: number): HTMLElement | null {
    const hit = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
    const b = hit?.closest<HTMLElement>('[data-slot]') ?? null;
    return b && this.bar.contains(b) ? b : null;
  }

  private light(b: HTMLElement | null) {
    if (b === this.lit) return;
    this.lit?.classList.remove('drop');
    b?.classList.add('drop');
    this.lit = b;
  }
}

/**
 * The toast of an assign (085 card 13): one line that names the fleet,
 * and UNDO while a replace can be turned back. It stands for
 * `undo_sec` seconds, and a new toast replaces the old one.
 */
export class SlotToast {
  readonly el: HTMLElement;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private undo: (() => void) | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'grptoast';
    this.el.setAttribute('role', 'status');
    // UNDO stands in fixed chrome, so it acts on the press (§16.8).
    this.el.addEventListener('pointerdown', (e) => {
      if (!(e.target as HTMLElement).closest('button')) return;
      e.preventDefault();
      this.fire();
    });
    this.el.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).closest('button')) {
        e.preventDefault();
        this.fire();
      }
    });
  }

  /** Shows one line of markup, with UNDO when `undo` is given. */
  show(html: string, undo?: () => void) {
    this.undo = undo ?? null;
    this.el.innerHTML = `<span>${html}</span>`
      + (undo ? `<button type="button" class="undo">${esc(S.slot_undo)}</button>` : '');
    this.el.classList.add('on');
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.hide(), GROUP_SLOTS_UI.undoSec * 1000);
  }

  /** Is UNDO on offer now? */
  get canUndo(): boolean { return this.undo !== null; }

  hide() {
    this.undo = null;
    this.el.classList.remove('on');
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private fire() {
    const u = this.undo;
    this.hide();
    u?.();
  }
}
